<template>
    <main
        v-if="!isLoading"
        id="js-page-content"
        class="post-v2"
        role="main"
    >
        <portal to="ariane">
            <nav class="tbv2-breadcrumb" aria-label="Breadcrumb">
                <router-link to="/posts" class="tbv2-breadcrumb-link">{{ $t('menu.tickets.ticket') }}</router-link>
                <span class="tbv2-breadcrumb-sep" aria-hidden="true">/</span>
                <span class="tbv2-breadcrumb-current">#{{ post.reference }}</span>
            </nav>
        </portal>

        <PostDetailsHeader
            @export-pdf="exportPDF()"
            :post="post"
            :loadingDownloadPdf="isPostExport"
        />

        <div class="post-layout">
            <div class="post-main">
                <PostInfo :post="post" />
                <PostContribution :post="post" />
                <PostAnswers />
                <PostMessages v-if="canExchangeWithCitizen" :post="post" id="messages" />
                <PostComments id="commentaires" />
            </div>

            <aside class="post-sidebar">
                <PostAdmin :post="post" />
                <PostUsers @get-post-data="getData" />
                <PostHistories id="histories" />
            </aside>
        </div>
        <div v-if="isPostExport" id="postExport">
            <div id="mapImgDiv"></div>
        </div>
    </main>
    <BmvLoader v-else v-model="isLoading" />
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import PostComments from '@/components/Model/Post/PostComments'
import PostMessages from '@/components/Model/Post/PostMessages'
import PostAnswers from '@/components/Model/Post/PostAnswers'
import PostHistories from '@/components/Model/Post/PostHistories'
import PostUsers from '@/components/Model/Post/PostUsers'
import PostDetailsHeader from '@/components/Model/Post/PostDetailsHeader'
import PostInfo from '@/components/Model/Post/PostInfo'
import PostContribution from '@/components/Model/Post/PostContribution'
import PostAdmin from '../../components/Model/Post/PostAdmin.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'

import domtoimage from 'dom-to-image-more'

export default {
    name: 'Post',
    components: {
        PostHistories,
        PostMessages,
        PostComments,
        PostAnswers,
        PostUsers,
        PostDetailsHeader,
        PostInfo,
        PostContribution,
        BmvLoader,
        PostAdmin
    },
    data: function () {
        return {
            modifStatus: false,
            divReponseCitoyen: false,
            buttonResponseCitoyen: true,
            isComment: true,
            isMessage: false,
            isPhoto: true,
            isModif: false,
            formAttributes: [],
            isLoading: true,
            isPostExport: false,
            baseUrl: this.$store.state.baseURL,
            token: this.$store.state.auth.token,
            filename: ' ',
            content: {}
        }
    },
    created() {
        this.getData()
    },
    computed: {
        ...mapState('post', {
            post: (state) => state.post,
            history: (state) => state.history
        }),
        hasAuthorEmail() {
            if (!this.post || !this.post.answers) return false
            return this.post.answers.some((answer) => {
                if (answer.name !== 'email' || !answer.config) return false
                try {
                    const config = typeof answer.config === 'string' ? JSON.parse(answer.config) : answer.config
                    return config.as === 'author_email'
                } catch (e) {
                    return false
                }
            })
        },
        canExchangeWithCitizen() {
            if (!this.post) return false
            // Des messages existent déjà (ex. citoyen anonyme ayant écrit) :
            // il faut toujours pouvoir les afficher, même sans compte ni email.
            if (this.post.messages && this.post.messages.length > 0) return true
            // Un citoyen avec un compte est joignable même s'il n'a pas
            // rempli de champ email dans le formulaire.
            if (this.post.author_id) return true
            return this.hasAuthorEmail
        },
        hasOperator: {
            get() {
                return this.$store.state.post.hasOperator
            },
            set(value) {
                this.$store.commit('post/setHasOperator', value)
            }
        },
        isPostRaciLoaded: {
            get() {
                return this.$store.state.post.isPostRaciLoaded
            },
            set(value) {
                this.$store.commit('post/setIsPostRaciLoaded', value)
            }
        }
    },
    methods: {
        ...mapActions('post', ['getPost', 'searchUsersList', 'exportPdfPost']),
        ...mapActions('status', ['getStatus']),
        delay(time) {
            return new Promise((resolve) => setTimeout(resolve, time))
        },
        exportPDF() {
            this.isPostExport = true
            var map = document.getElementById('myMap')
            if (map) {
                map.style.height = 'calc(100vh - 500px)'
                this.delay(3000).then(() => {
                    domtoimage.toPng(map).then(function (dataUrl) {
                        var img = new Image()
                        img.src = dataUrl
                        img.setAttribute('id', 'mapImg')
                        var divImg = document.getElementById('mapImgDiv')
                        divImg.appendChild(img)
                        map.style.height = 'calc(100vh - 330px)'
                    })
                    this.exportPDFHandler()
                })
            } else {
                this.exportPDFHandler()
            }
        },
        exportPDFHandler() {
            this.delay(3000).then(() => {
                var divImg = document.getElementById('mapImg')
                var mapView = null
                if (divImg) {
                    var pdfView = {
                        name: `map_img_post_${this.post.id}`,
                        ext: `png`,
                        encodedFile: divImg.src
                    }
                    mapView = { mapImg: pdfView }
                }
                this.exportPdfPost(mapView)
                    .then(() => {
                        const link = document.createElement('a')
                        link.href = `${this.baseUrl}/storage/posts/${this.post.reference}/export/pdf/download?token=${this.token}`
                        link.download
                        link.click()
                    })
                    .finally(() => {
                        this.isPostExport = false
                    })
            })
        },
        getData() {
            let getPostPromise = this.getPost(this.$route.params.id).then(
                () => {
                    let racis = Object.assign([], this.post.racis)
                    if (racis) {
                        let operator = racis.filter(
                            (element) => element.is_responsible == 1
                        )[0]
                        if (operator) {
                            this.hasOperator = true
                        } else {
                            this.hasOperator = false
                        }
                        racis.sort((a, b) => {
                            if (a.is_responsible) {
                                this.hasOperator = true
                                return -1
                            }
                            if (b.is_responsible) {
                                this.hasOperator = true
                                return 1
                            }
                            return 0
                        })
                        this.isPostRaciLoaded = true
                    }
                }
            )
            let getStatusPromise = this.getStatus()
            Promise.all([getPostPromise, getStatusPromise]).then(() => {
                this.isLoading = false
            })
        }
    },

    beforeDestroy() {
        this.$store.commit('post/setPost', null)
        this.$store.commit('post/setHistory', null)
        this.$store.commit('post/setHasOperator', false)
    }
}
</script>

<style scoped>
.post-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-breadcrumb {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    font-weight: var(--v2-font-weight-medium);
}

.tbv2-breadcrumb-link {
    color: var(--v2-text-3);
    text-decoration: none;
    transition: color var(--v2-transition-fast);
}

.tbv2-breadcrumb-link:hover {
    color: var(--v2-primary);
}

.tbv2-breadcrumb-sep {
    color: var(--v2-text-4);
    font-weight: 400;
}

.tbv2-breadcrumb-current {
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-semibold);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.005em;
}

.post-layout {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: var(--v2-space-4);
    margin-top: var(--v2-space-4);
}

.post-main {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
    min-width: 0;
}

.post-sidebar {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
}

@media (max-width: 1200px) {
    .post-layout {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 900px) {
    .post-v2 {
        padding: 16px 18px 32px;
    }
}
</style>

<style>
#postExport {
    display: none;
}
</style>
